import type { Filter, Filters } from './filters';
import {
  type LabelFilter,
  displayTokenToLabelFilter,
  isNameOnlyLabelFilter,
  labelFilterToDisplayToken,
  parseDisplayTokensToLabelFilters,
} from './labelFilterValue';

export type LabelsContainsOperator = 'containsAny' | 'notContainsAny';

export const normalizeLabelDisplayToken = (input: string): string =>
  labelFilterToDisplayToken(displayTokenToLabelFilter(input));

export const parseNameOnlyLabelFilters = (raw: string): LabelFilter[] =>
  raw
    .split(',')
    .map((name) => name.trim())
    .filter((name) => name !== '')
    .map((name) => ({ name }));

export const labelFiltersToAndFilters = (
  columnKey: string,
  operator: LabelsContainsOperator,
  labelFilters: LabelFilter[],
): Filters | undefined => {
  if (labelFilters.length === 0) {
    return undefined;
  }

  if (labelFilters.length === 1) {
    return {
      [columnKey]: [operator, [labelFilters[0]!]] satisfies Filter,
    };
  }

  return {
    and: labelFilters.map(
      (labelFilter) =>
        ({
          [columnKey]: [operator, [labelFilter]],
        }) as Filters,
    ),
  };
};

export const displayTokensToLabelsAndFilters = (
  columnKey: string,
  operator: LabelsContainsOperator,
  raw: string,
): Filters | undefined =>
  labelFiltersToAndFilters(
    columnKey,
    operator,
    parseDisplayTokensToLabelFilters(raw),
  );

export const nameTokensToLabelsAndFilters = (
  columnKey: string,
  operator: LabelsContainsOperator,
  raw: string,
): Filters | undefined =>
  labelFiltersToAndFilters(columnKey, operator, parseNameOnlyLabelFilters(raw));

/**
 * Simple-filter name-only tokens (`critical`) match name OR value.
 * Name:value tokens (`region: cn`) stay exact pair matches.
 */
const simpleLabelFilterToFilters = (
  columnKey: string,
  labelFilter: LabelFilter,
): Filters => {
  if (isNameOnlyLabelFilter(labelFilter)) {
    return {
      or: [
        {
          [columnKey]: [
            'containsAny',
            [{ name: labelFilter.name }],
          ] satisfies Filter,
        },
        {
          [columnKey]: [
            'containsAny',
            [{ value: labelFilter.name }],
          ] satisfies Filter,
        },
      ],
    };
  }

  return {
    [columnKey]: ['containsAny', [labelFilter]] satisfies Filter,
  };
};

/**
 * Apply a labels quick-filter string onto an accumulating Filters object.
 * Comma-separated tokens become AND-ed includes filters.
 * Tokens without `:` match name or value.
 */
export const appendSimpleLabelsFilters = (
  acc: Filters,
  columnKey: string,
  raw: string,
): void => {
  const labelFilters = parseDisplayTokensToLabelFilters(raw);
  if (labelFilters.length === 0) {
    return;
  }

  const labelsFilters =
    labelFilters.length === 1
      ? simpleLabelFilterToFilters(columnKey, labelFilters[0]!)
      : {
          and: labelFilters.map((labelFilter) =>
            simpleLabelFilterToFilters(columnKey, labelFilter),
          ),
        };

  if (labelsFilters.and) {
    acc.and = [...(acc.and ?? []), ...labelsFilters.and];
    return;
  }

  Object.assign(acc, labelsFilters);
};
